import React, { useState } from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from '../components/Icons';
import Layout from '../components/Layout';
import ContactFormDialog from '../components/ContactFormDialog';
import { PRODUCT_URLS } from '../constants';

const eyebrow = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: 0.6,
  mb: 2.5,
  borderRadius: 999,
  border: '1px solid rgba(14,165,233,0.22)',
  bgcolor: 'rgba(14,165,233,0.06)',
  color: 'primary.dark',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background:
            'radial-gradient(ellipse 90% 55% at 50% -8%, rgba(14,165,233,0.13), transparent 58%)',
        }}
      >
        <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 6, md: 8 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
              gap: { xs: 6, md: 8 },
              alignItems: 'center',
              animation: 'fadeUp 0.7s ease both',
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={eyebrow}>AI technology</Box>
              <Typography
                variant="h1"
                sx={{ mb: 3, fontSize: { xs: '2.55rem', sm: '3.4rem', md: '4rem' }, color: '#0f172a' }}
              >
                Technology that
                <br />
                moves industries.
              </Typography>
              <Typography
                sx={{
                  color: '#334155',
                  fontSize: { xs: 16, md: 18 },
                  maxWidth: 520,
                  mb: 4,
                }}
              >
                Zendi builds AI systems for real work. Right now that means Companion for
                personal wellness, iPOS for clinics, and AdamCore diagnostic experts underneath —
                with more industries ahead.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowUpRight />}
                  component="a"
                  href={PRODUCT_URLS.companion}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Companion
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  endIcon={<ArrowUpRight />}
                  component="a"
                  href={PRODUCT_URLS.ipos}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ borderColor: 'rgba(15,23,42,0.16)', color: '#0f172a' }}
                >
                  Open iPOS
                </Button>
              </Stack>
            </Box>

            <HeroStage />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 680, mb: 6 }}>
          <Box sx={eyebrow}>Products</Box>
          <Typography variant="h2" sx={{ mb: 1.5 }}>
            Wellness for people. Tools for clinics. Experts underneath.
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 17 }}>
            Companion is our Wellness Companion app — not just another health tracker. It runs on our
            proprietary Eat–Move–Care–Restore wellness model, with Adam AI engines including AdamCore
            where diagnostics matter.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
            gap: 2.5,
            mb: 2.5,
          }}
        >
          <QuietCard
            status="Wellness Companion"
            title="Companion"
            body="Holistic wellness for individuals. Built around Eat–Move–Care–Restore: what you eat, how you move, how you care for body and mind, and how you restore for longevity."
            action={
              <Button
                component="a"
                href={PRODUCT_URLS.companion}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ArrowUpRight />}
                sx={{ mt: 2, px: 0 }}
              >
                Open app
              </Button>
            }
          />
          <QuietCard
            status="Clinic"
            title="iPOS"
            body="Intelligent Point of Service for providers. Scheduling, records, and AdamCore diagnostics in one clinical workflow."
            action={
              <Button
                component="a"
                href={PRODUCT_URLS.ipos}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ArrowUpRight />}
                sx={{ mt: 2, px: 0 }}
              >
                Open iPOS
              </Button>
            }
          />
          <QuietCard
            status="Experts"
            title="AdamCore"
            body="Condition-specific diagnostic experts — type 2 diabetes, hypertension, coronary risk, and more — already powering both apps."
          />
        </Box>
      </Container>

      <Box sx={{ bgcolor: '#f8fafc', borderTop: '1px solid rgba(15,23,42,0.06)', borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ maxWidth: 640, mb: 6 }}>
            <Box sx={eyebrow}>Companion</Box>
            <Typography variant="h2" sx={{ mb: 1.5 }}>
              Eat. Move. Care. Restore.
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 17, maxWidth: 640 }}>
              Our proprietary wellness model — not an AI model — for tracking and managing holistic
              wellness: what you are Eating, how well you are Moving, the practices you use to Care
              for physical and mental health, and how you Restore body and mind for a longer,
              fuller life.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 5,
            }}
          >
            {[
              { title: 'Eat', body: 'What you put in — meals, habits, nutrition patterns.' },
              { title: 'Move', body: 'How you move through the day and train your body.' },
              { title: 'Care', body: 'Practices that look after physical and mental health.' },
              { title: 'Restore', body: 'Rest, recovery, and longevity for a fulfilling life.' },
            ].map((pillar) => (
              <Box
                key={pillar.title}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid rgba(15,23,42,0.08)',
                  bgcolor: '#fff',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.75, letterSpacing: '-0.02em' }}>
                  {pillar.title}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>{pillar.body}</Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <ProductCard
              kicker="Wellness Companion"
              title="Companion"
              body="A wellness app for everyday life. Eat–Move–Care–Restore for the whole person, with AdamCore when diagnostic insight is needed."
              points={['Eat–Move–Care–Restore wellness model', 'Adam AI + AdamCore experts', 'Private by design']}
              href={PRODUCT_URLS.companion}
              cta="Open Companion"
            />
            <ProductCard
              kicker="Clinics"
              title="iPOS"
              body="Scheduling, records, and AdamCore diagnostics in one provider platform."
              points={['AdamCore in clinic', 'Wellness records', 'Population views']}
              href={PRODUCT_URLS.ipos}
              cta="Open iPOS"
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 640, mb: 6 }}>
          <Box sx={eyebrow}>Clients</Box>
          <Typography variant="h2" sx={{ mb: 1.5 }}>
            Member apps for medical aids
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 17 }}>
            White-label builds of Companion, trimmed to what each scheme has licensed.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5,
            mb: 2.5,
          }}
        >
          <QuietCard
            status="Signed"
            title="ParksMed"
            body="Live member application based on Companion, scoped to ParksMed benefits."
          />
          <QuietCard
            status="Next"
            title="Generation Health"
            body="Next medical-aid build on the same Companion foundation."
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
            gap: 2.5,
          }}
        >
          <QuietCard
            status="Ahead"
            title="Developer access"
            body="As AdamHealth grows, we plan API access so other teams can test AdamCore experts."
          />
          <QuietCard
            status="Hardware"
            title="Shop"
            body="Devices and kits we sell alongside software deployments."
            action={
              <Button onClick={() => navigate('/shop')} endIcon={<ArrowRight />} sx={{ mt: 2, px: 0 }}>
                Visit shop
              </Button>
            }
          />
          <QuietCard
            status="Also"
            title="ziPOS"
            body="Our retail point-of-sale system — another product in the Zendi lineup."
          />
        </Box>
      </Container>

      <Container maxWidth="md" sx={{ pb: { xs: 10, md: 14 } }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Working on something together?
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, maxWidth: 480, mx: 'auto' }}>
            Medical aids, clinics, partners, or anyone exploring AdamCore — get in touch.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <Button variant="contained" size="large" onClick={() => setContactOpen(true)}>
              Talk with us
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/products')}>
              See products
            </Button>
          </Stack>
        </Box>
      </Container>

      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Talk with Zendi"
        description="Tell us what you need — AdamCore, Companion, iPOS, a member app, or something else."
      />
    </Layout>
  );
};

const HeroStage: React.FC = () => (
  <Box sx={{ position: 'relative', minHeight: { xs: 360, md: 440 } }}>
    <Box
      sx={{
        position: 'absolute',
        inset: { xs: 20, md: 12 },
        borderRadius: 4,
        border: '1px solid rgba(15,23,42,0.1)',
        bgcolor: '#fff',
        boxShadow: '0 24px 60px rgba(15,23,42,0.1)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: 44,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid rgba(15,23,42,0.06)',
        }}
      >
        {['#fda4af', '#fcd34d', '#86efac'].map((c) => (
          <Box key={c} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c }} />
        ))}
        <Typography sx={{ ml: 1.5, fontSize: 12, color: 'text.secondary', fontWeight: 600 }}>
          zendi · adam stack
        </Typography>
      </Box>
      <Box sx={{ p: 2.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <MiniPanel
          label="Companion"
          value="Wellness"
          rows={[
            ['Eat · Move', 'Tracked'],
            ['Care · Restore', 'Active'],
            ['AdamCore', 'Ready'],
          ]}
        />
        <MiniPanel
          label="iPOS"
          value="Clinic"
          rows={[
            ['Waiting room', '4'],
            ['Diagnostics', 'AdamCore'],
            ['Records', 'Live'],
          ]}
        />
      </Box>
    </Box>
    <StatFloat label="Companion" value="Live" top={8} left={0} />
    <StatFloat label="iPOS" value="Live" bottom={16} right={0} />
  </Box>
);

const MiniPanel: React.FC<{
  label: string;
  value: string;
  rows: [string, string][];
}> = ({ label, value, rows }) => (
  <Box
    sx={{
      p: 1.75,
      borderRadius: 2.5,
      border: '1px solid rgba(15,23,42,0.07)',
      bgcolor: '#f8fafc',
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.dark', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 700, fontSize: 18, letterSpacing: '-0.03em', mb: 1.5 }}>
      {value}
    </Typography>
    {rows.map(([k, v]) => (
      <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.55 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{k}</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{v}</Typography>
      </Box>
    ))}
  </Box>
);

const StatFloat: React.FC<{
  label: string;
  value: string;
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}> = ({ label, value, ...pos }) => (
  <Box
    sx={{
      position: 'absolute',
      ...pos,
      px: 1.75,
      py: 1.1,
      bgcolor: '#fff',
      border: '1px solid rgba(15,23,42,0.08)',
      borderRadius: 2.5,
      boxShadow: '0 12px 32px rgba(15,23,42,0.08)',
      animation: 'floatY 6s ease-in-out infinite',
    }}
  >
    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'success.main' }}>{value}</Typography>
  </Box>
);

const ProductCard: React.FC<{
  kicker: string;
  title: string;
  body: string;
  points: string[];
  href: string;
  cta: string;
}> = ({ kicker, title, body, points, href, cta }) => (
  <Box
    sx={{
      p: { xs: 3, md: 4 },
      borderRadius: 4,
      border: '1px solid rgba(15,23,42,0.08)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 18px 40px rgba(15,23,42,0.07)',
      },
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'primary.dark', mb: 1.5 }}>
      {kicker}
    </Typography>
    <Typography variant="h3" sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    <Typography sx={{ color: 'text.secondary', mb: 3 }}>{body}</Typography>
    <Stack spacing={1.1} sx={{ mb: 3, flexGrow: 1 }}>
      {points.map((point) => (
        <Box key={point} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: '7px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 14, color: 'text.primary' }}>{point}</Typography>
        </Box>
      ))}
    </Stack>
    <Button
      variant="contained"
      endIcon={<ArrowUpRight />}
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ alignSelf: 'flex-start' }}
    >
      {cta}
    </Button>
  </Box>
);

const QuietCard: React.FC<{
  status: string;
  title: string;
  body: string;
  action?: React.ReactNode;
}> = ({ status, title, body, action }) => (
  <Box
    sx={{
      p: { xs: 3, md: 3.5 },
      borderRadius: 4,
      border: '1px solid rgba(15,23,42,0.08)',
      bgcolor: '#fff',
      height: '100%',
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 1.25 }}>
      {status}
    </Typography>
    <Typography variant="h4" sx={{ mb: 1.25 }}>
      {title}
    </Typography>
    <Typography sx={{ color: 'text.secondary', fontSize: 15 }}>{body}</Typography>
    {action}
  </Box>
);

export default Home;
